import React, { useEffect, useState } from "react";
import { api, fmtDate, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Printer, Pencil, ClipboardCheck } from "lucide-react";
import { SearchBox, DeleteButton, matchesQuery } from "@/components/crm/RecordActions";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const empty = {
  aee_office: "The Assistant Executive Engineer",
  discom_name: "JVVNL",
  kw_capacity: "",
  system_address: "",
  k_number: "",
  ac_number: "",
  customer_name: "",
  customer_city: "",
  customer_id: "",
  installation_date: new Date().toISOString().slice(0, 10),
  commissioning_date: new Date().toISOString().slice(0, 10),
  pv_model_no: "", pv_total_capacity_kwp: "", pv_make: "",
  pv_num_modules: "", pv_each_capacity_kwp: "",
  inv_make: "", inv_brand: "", inv_model_no: "", inv_capacity_kwp: "",
  inv_serial_no: "", inv_installation_date: "", inv_commissioning_date: "",
  dc_cable_make: "Polycab", dc_cable_size_type: "4 sq.mm, DC",
  ac_cable_make: "Polycab", ac_cable_size_type: "2C x 4 sq.mm, AC",
  dcdb_make: "", dcdb_serial_no: "", dcdb_spd: "", dcdb_mcb: "", dcdb_size_type: "",
  acdb_make: "", acdb_serial_no: "", acdb_spd: "", acdb_mcb: "", acdb_size_type: "",
  earth_resistance: "< 2 Ohm", earth_wire_size: "10 sq.mm Cu",
  earth_three_points: "Yes", earth_size_type: "GI/Cu Bonded 2m x 3 Nos",
  meter_make: "", meter_serial_no: "", meter_capacity: "", meter_type: "",
  meter_phase: "Single Phase", meter_ct_rated: "-", meter_ct_serial: "-", meter_test_date: "",
  caution_signs: "Provided at required locations",
  manual_auto_switches: "Provided with MCB & Isolators",
  gps_coordinates: "", gps_lat: "", gps_lng: "",
  om_info: "O&M manual, Do's & Don'ts booklet handed over to customer",
  installer_name: "", installer_designation: "Authorised Signatory",
  notes: "",
};

export default function WorkCompletion() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    setItems((await api.get("/wcr")).data);
    try { setCustomers((await api.get("/customers")).data); } catch { setCustomers([]); }
  };
  useEffect(() => { load(); }, []);

  const pickCustomer = (cid) => {
    const c = customers.find(x => x.id === cid);
    if (!c) return;
    setForm(f => ({ ...f, customer_id: cid, customer_name: c.name, customer_city: c.city || "", system_address: c.address || "" }));
  };

  const save = async () => {
    try {
      if (editId) await api.patch(`/wcr/${editId}`, form);
      else await api.post("/wcr", form);
      toast.success(editId ? "Report updated" : "Work completion report created");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const edit = (w) => { setForm({ ...empty, ...w }); setEditId(w.id); setOpen(true); };
  const del = async (w) => {
    try { await api.delete(`/wcr/${w.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const visible = items.filter(w => matchesQuery(w, search));

  const setF = (k, v) => setForm({ ...form, [k]: v });
  const F = (k, label, placeholder) => (
    <div key={k}>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <Input value={form[k] ?? ""} onChange={e => setF(k, e.target.value)} placeholder={placeholder || ""} data-testid={`wcr-${k}`} className="mt-1" />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="wcr-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Work Completion Report <span className="text-slate-400 text-lg font-medium">/ DESCOM रिपोर्ट</span>
          </h1>
          <p className="text-slate-500 text-sm">DESCOM submission — K.No, KW rating, module & meter specs.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SearchBox value={search} onChange={setSearch} placeholder="Search K.No, customer..." testid="wcr-search-input" />
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-wcr-btn"><Plus className="w-4 h-4 mr-1" />New Report</Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-amber-600" />
                  {editId ? "Edit" : "New"} Work Completion Report
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <Section title="A. To (DESCOM Office)">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {F("aee_office", "Office", "The Assistant Executive Engineer")}
                    {F("discom_name", "DESCOM Name", "JVVNL / AVVNL / JDVVNL")}
                  </div>
                </Section>
                <Section title="B. Customer Reference">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Customer (existing)</Label>
                      <Select value={form.customer_id || ""} onValueChange={pickCustomer}>
                        <SelectTrigger data-testid="wcr-customer-select"><SelectValue placeholder="Pick or fill manually" /></SelectTrigger>
                        <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} — {c.mobile}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {F("customer_name", "Customer Name")}
                    {F("kw_capacity", "KW Rating", "e.g. 5")}
                    {F("system_address", "System / Site Address")}
                    {F("customer_city", "City")}
                    {F("k_number", "K No.")}
                    {F("ac_number", "A/c No.")}
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Date of Installation</Label>
                      <Input type="date" value={form.installation_date || ""} onChange={e => setF("installation_date", e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Date of Commissioning</Label>
                      <Input type="date" value={form.commissioning_date || ""} onChange={e => setF("commissioning_date", e.target.value)} className="mt-1" />
                    </div>
                  </div>
                </Section>
                <Section title="1. PV Module Details">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {F("pv_make", "Make / Manufacture Name", "Waaree / Adani / Vikram")}
                    {F("pv_model_no", "Model No.")}
                    {F("pv_total_capacity_kwp", "Total Capacity (KWp)")}
                    {F("pv_num_modules", "No. of Modules")}
                    {F("pv_each_capacity_kwp", "Capacity of Each Module (KWp)")}
                  </div>
                </Section>
                <Section title="2. Inverter Details">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {F("inv_make", "Manufacturer")}
                    {F("inv_brand", "Brand Name")}
                    {F("inv_model_no", "Model No.")}
                    {F("inv_capacity_kwp", "Capacity (KWp)")}
                    {F("inv_serial_no", "Serial No.")}
                    <div><Label className="text-xs font-semibold text-slate-600">Date of Installation</Label>
                      <Input type="date" value={form.inv_installation_date || ""} onChange={e => setF("inv_installation_date", e.target.value)} className="mt-1" /></div>
                    <div><Label className="text-xs font-semibold text-slate-600">Date of Commissioning</Label>
                      <Input type="date" value={form.inv_commissioning_date || ""} onChange={e => setF("inv_commissioning_date", e.target.value)} className="mt-1" /></div>
                  </div>
                </Section>
                <Section title="3-4. Cables (DC & AC)">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {F("dc_cable_make", "DC Cable Make")}
                    {F("dc_cable_size_type", "DC Size & Type")}
                    {F("ac_cable_make", "AC Cable Make")}
                    {F("ac_cable_size_type", "AC Size & Type")}
                  </div>
                </Section>
                <Section title="5. DC Distribution Box">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {F("dcdb_make", "Make")}
                    {F("dcdb_serial_no", "Serial No")}
                    {F("dcdb_spd", "DC SPD")}
                    {F("dcdb_mcb", "MCB Qty & Capacity")}
                    {F("dcdb_size_type", "Size & Type")}
                  </div>
                </Section>
                <Section title="6. AC Distribution Box">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {F("acdb_make", "Make")}
                    {F("acdb_serial_no", "Serial No")}
                    {F("acdb_spd", "AC SPD")}
                    {F("acdb_mcb", "MCB Qty & Capacity")}
                    {F("acdb_size_type", "Size & Type")}
                  </div>
                </Section>
                <Section title="7. Earthing">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {F("earth_resistance", "Earth Resistance (<5Ω)")}
                    {F("earth_wire_size", "Earth Wire / Flat Size")}
                    {F("earth_three_points", "3 Separate Earthing Points?")}
                    {F("earth_size_type", "Size & Type")}
                  </div>
                </Section>
                <Section title="8. Bi-directional Meter & CTs">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {F("meter_make", "Make")}
                    {F("meter_serial_no", "Serial No")}
                    {F("meter_capacity", "Capacity")}
                    {F("meter_type", "Type / Model")}
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Phase</Label>
                      <Select value={form.meter_phase} onValueChange={v => setF("meter_phase", v)}>
                        <SelectTrigger className="mt-1" data-testid="wcr-meter-phase-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Single Phase">Single Phase</SelectItem>
                          <SelectItem value="Three Phase">Three Phase</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {F("meter_ct_rated", "CT Rated")}
                    {F("meter_ct_serial", "CT Serial No")}
                    <div><Label className="text-xs font-semibold text-slate-600">Date of Test by MT DESCOM</Label>
                      <Input type="date" value={form.meter_test_date || ""} onChange={e => setF("meter_test_date", e.target.value)} className="mt-1" /></div>
                  </div>
                </Section>
                <Section title="9-13. Safety, GPS & O&M">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {F("caution_signs", "Caution Signs")}
                    {F("manual_auto_switches", "Manual/Auto Switches")}
                    {F("gps_lat", "GPS Latitude")}
                    {F("gps_lng", "GPS Longitude")}
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-semibold text-slate-600">GPS Co-ordinates (combined)</Label>
                      <Input value={form.gps_coordinates || ""} onChange={e => setF("gps_coordinates", e.target.value)} placeholder="e.g. 26.5643 N, 75.8231 E" className="mt-1" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-semibold text-slate-600">O&M Information Handover</Label>
                      <Textarea rows={2} value={form.om_info || ""} onChange={e => setF("om_info", e.target.value)} className="mt-1" />
                    </div>
                  </div>
                </Section>
                <Section title="Signature Block">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {F("installer_name", "Installer Name (blank = use logged-in user)")}
                    {F("installer_designation", "Designation")}
                  </div>
                </Section>
                <Section title="Notes">
                  <Textarea rows={2} value={form.notes || ""} onChange={e => setF("notes", e.target.value)} placeholder="Any additional remark" data-testid="wcr-notes" />
                </Section>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-amber-500 hover:bg-amber-600" onClick={save} disabled={!form.customer_name || !form.kw_capacity} data-testid="save-wcr-btn">
                  {editId ? "Update Report" : "Create Report"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>WCR #</TableHead><TableHead>K No.</TableHead><TableHead>Customer</TableHead>
            <TableHead>KW</TableHead><TableHead>City</TableHead><TableHead>Date</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {visible.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">{search ? `No matches for "${search}"` : "No reports yet."}</TableCell></TableRow>}
            {visible.map(w => (
              <TableRow key={w.id} data-testid={`wcr-row-${w.wcr_no}`}>
                <TableCell className="font-mono text-xs font-semibold">{w.wcr_no}</TableCell>
                <TableCell className="font-mono text-xs">{w.k_number || "-"}</TableCell>
                <TableCell>{w.customer_name}<div className="text-xs text-slate-500">{w.discom_name}</div></TableCell>
                <TableCell className="font-semibold">{w.kw_capacity ? `${w.kw_capacity} KW` : "-"}</TableCell>
                <TableCell>{w.customer_city || "-"}</TableCell>
                <TableCell className="text-xs">{fmtDate(w.commissioning_date || w.created_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Link to={`/work-completion/${w.id}/print`}>
                      <Button size="sm" variant="outline" data-testid={`print-wcr-${w.wcr_no}`}><Printer className="w-3 h-3 mr-1" />Print</Button>
                    </Link>
                    <Button size="sm" variant="ghost" onClick={() => edit(w)} data-testid={`edit-wcr-${w.wcr_no}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    {user?.role === "admin" && (
                      <DeleteButton title={`Delete WCR ${w.wcr_no}?`} description={`This will permanently delete this report.`} onConfirm={() => del(w)} testid={`delete-wcr-${w.wcr_no}`} />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 border-b border-slate-200 pb-1 mb-2">{title}</div>
      {children}
    </div>
  );
}
